import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { supabase } from "./supabase";

/**
 * Who's signed in, which profile that is, every society they belong to,
 * and which one they've chosen to open.
 *
 * Nothing is chosen automatically. After signing in a member lands on the
 * welcome screen and picks a society. The choice lives in sessionStorage,
 * so a refresh keeps you where you were, but opening the app afresh —
 * including relaunching it from the Home Screen — brings the welcome back.
 *
 * The last society opened is kept separately, in localStorage, only so the
 * welcome screen can mark it.
 */

const CURRENT = "postcard.society.current";   // this session
const LAST = "postcard.society.last";         // across sessions, for display only

const Ctx = createContext(null);
export const useSession = () => useContext(Ctx);

const read = (store, key) => { try { return store.getItem(key); } catch { return null; } };
const write = (store, key, v) => { try { v == null ? store.removeItem(key) : store.setItem(key, v); } catch {} };

export function SessionProvider({ children }) {
  const [state, setState] = useState({
    loading: true, user: null, profile: null, platformAdmin: false,
    societies: [], society: null, role: null,
  });

  const load = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      setState({ loading: false, user: null, profile: null, platformAdmin: false,
                 societies: [], society: null, role: null });
      return [];
    }

    const { data: profile } = await supabase
      .from("profiles").select("*").eq("user_id", session.user.id).maybeSingle();

    let platformAdmin = false;
    if (profile) {
      const { data: pa } = await supabase
        .from("platform_admins").select("profile_id").eq("profile_id", profile.id).maybeSingle();
      platformAdmin = !!pa;
    }

    let societies = [];
    if (profile) {
      const { data: rows } = await supabase
        .from("society_members")
        .select("role, joined_at, societies(*)")
        .eq("profile_id", profile.id)
        .order("joined_at");
      societies = (rows ?? []).map((r) => ({ ...r.societies, role: r.role }));
    }

    const chosen = read(sessionStorage, CURRENT);
    const society = societies.find((s) => s.id === chosen) ?? null;

    setState({ loading: false, user: session.user, profile, societies, platformAdmin,
               society, role: society?.role ?? null });
    return societies;       // so a caller can open one it has just joined
  }, []);

  useEffect(() => {
    load();
    const { data: sub } = supabase.auth.onAuthStateChange(() => load());
    return () => sub.subscription.unsubscribe();
  }, [load]);

  /** Open a society. Remembered for this session, and marked as last opened. */
  const chooseSociety = (id, list) => {
    const pool = list ?? state.societies;
    const next = pool.find((s) => s.id === id);
    if (!next) return;
    write(sessionStorage, CURRENT, id);
    write(localStorage, LAST, id);
    setState((s) => ({ ...s, societies: pool, society: next, role: next.role }));
  };

  /** Back to the welcome screen. */
  const leaveSociety = () => {
    write(sessionStorage, CURRENT, null);
    setState((s) => ({ ...s, society: null, role: null }));
  };

  const value = {
    ...state,
    lastSocietyId: read(localStorage, LAST),
    refresh: load,
    chooseSociety,
    leaveSociety,
    // A platform admin passes every check, in the app as in the database.
    isAdmin: state.platformAdmin || state.role === "admin",
    isOrganiser: state.platformAdmin || state.role === "admin" || state.role === "organiser",
    isPlatformAdmin: state.platformAdmin,
    signOut: async () => {
      write(sessionStorage, CURRENT, null);
      await supabase.auth.signOut();
    },
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
